import { ComposableCssVariablesMap } from '../../types/css-variable-key-list';
import { PaymentComponentConfig } from '../../types/external/payment-component';

export function getDarkerLightenColor(color: string, percent: number): string {
  const [r, g, b] = getRGBFromHex(color);
  const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
  let shiftAmount;
  if (brightness < 128) {
    shiftAmount = Math.round(2.55 * Math.abs(percent));
  } else {
    shiftAmount = -Math.round(2.55 * Math.abs(percent));
  }
  const shiftedR = Math.max(0, Math.min(255, r + shiftAmount));
  const shiftedG = Math.max(0, Math.min(255, g + shiftAmount));
  const shiftedB = Math.max(0, Math.min(255, b + shiftAmount));
  return '#' + numToHexString(shiftedR) + numToHexString(shiftedG) + numToHexString(shiftedB);
}
export function isHEXFormat(color: string) {
  const HEX_COLOR_REGEXP = /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;
  return HEX_COLOR_REGEXP.test(color);
}
function getRGBFromHex(hex: string): [number, number, number] {
  let c = hex.substring(1);
  if (c.length === 3) {
    c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  }
  const num = Number.parseInt(c, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}
function numToHexString(num: number): string {
  const clamped = Math.max(0, Math.min(255, num));
  return clamped.toString(16).padStart(2, '0');
}
export function getContrastColor(color: string): string {
  const contrastThreshold = 128,
    Y = 0.299,
    I = 0.587,
    Q = 0.114,
    blackHEX = '#000000',
    whiteHEX = '#ffffff';
  const [r, g, b] = getRGBFromHex(color);
  return r * Y + g * I + b * Q > contrastThreshold ? blackHEX : whiteHEX;
}
function clampRadiusValues(min: number, max: number, value?: string): string | undefined {
  if (value == null) {
    return undefined;
  }
  const numeric = typeof value === 'number' ? value : Number.parseFloat(value.trim());
  if (!Number.isFinite(numeric)) {
    return undefined;
  }
  const clamped = Math.max(min, Math.min(max, numeric));
  return `${clamped}px`;
}
export function clampRemSizes(min: number, max: number, value?: string): string | undefined {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return undefined;
  }
  const numeric = typeof value === 'number' ? value : Number.parseFloat(value.trim());
  if (!Number.isFinite(numeric)) {
    return undefined;
  }
  const clamped = Math.max(min, Math.min(max, numeric));
  return `${clamped}rem`;
}

export const defaultCssVariablesList: ComposableCssVariablesMap = {
  'color-primary': '#006D72',
  'color-primary-hover': getDarkerLightenColor('#006D72', 12),
  'color-primary-active': getDarkerLightenColor('#006D72', 20),
  'color-primary-contrast': getContrastColor('#006D72'),
  'primary-font': "'Open Sans', Arial, Helvetica, sans-serif",
  'font-size': '1rem',
  'button-border-radius': '4px',
  'card-radius': '4px',
  'input-radius': '4px'
};

export function getCssVariablesList(styling?: PaymentComponentConfig['styling']): ComposableCssVariablesMap {
  const {
    'color-primary': primaryColor,
    'primary-font': primaryFont,
    'font-size': fontSize,
    'button-border-radius': buttonBorderRadius,
    'card-radius': cardRadius,
    'input-radius': inputRadius
  } = styling ?? {};
  return {
    'color-primary': primaryColor && isHEXFormat(primaryColor) ? primaryColor : defaultCssVariablesList['color-primary'],
    'color-primary-hover':
      primaryColor && isHEXFormat(primaryColor) ? getDarkerLightenColor(primaryColor, 12) : defaultCssVariablesList['color-primary-hover'],
    'color-primary-active':
      primaryColor && isHEXFormat(primaryColor) ? getDarkerLightenColor(primaryColor, 20) : defaultCssVariablesList['color-primary-active'],
    'color-primary-contrast':
      primaryColor && isHEXFormat(primaryColor) ? getContrastColor(primaryColor) : defaultCssVariablesList['color-primary-contrast'],
    'primary-font': primaryFont || defaultCssVariablesList['primary-font'],
    'font-size': clampRemSizes(0.85, 1.25, fontSize) || defaultCssVariablesList['font-size'],
    'button-border-radius': clampRadiusValues(0, 64, buttonBorderRadius) || defaultCssVariablesList['button-border-radius'],
    'card-radius': clampRadiusValues(0, 24, cardRadius) || defaultCssVariablesList['card-radius'],
    'input-radius': clampRadiusValues(0, 16, inputRadius) || defaultCssVariablesList['input-radius']
  };
}
